import { createEmptyOmniDraft } from './types'
import type { OmniAsset, OmniDraft } from './types'
import type { Document } from '~publish/legacy/composer/composer/entities/Document'
import type {
  Gif,
  Image,
  Link,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'

/**
 * Returns a deep copy of an OmniDraft with every nested object and array
 * cloned, so mutations on the result cannot reach the source.
 *
 * structuredClone is not available in the jsdom test environment used by this
 * project, so the copy is built with explicit field-by-field mapping.
 */
export function serializeOmni(omni: OmniDraft): OmniDraft {
  return {
    id: 'omni',
    text: omni.text,
    urls: [...omni.urls],
    unshortenedUrls: [...omni.unshortenedUrls],
    assets: omni.assets.map(cloneOmniAsset),
    aiAssisted: omni.aiAssisted,
    draftId: omni.draftId,
    title: omni.title,
  }
}

function cloneImage(image: Image): Image {
  return {
    ...image,
    userTags: image.userTags
      ? image.userTags.map((t) => ({ ...t }))
      : undefined,
  }
}

function isStringArray(arr: Image[] | string[]): arr is string[] {
  return arr.length === 0 || typeof arr[0] === 'string'
}

export function cloneOmniAsset(asset: OmniAsset): OmniAsset {
  switch (asset.kind) {
    case 'image':
      return { kind: 'image', image: cloneImage(asset.image) }
    case 'video':
      return {
        kind: 'video',
        video: {
          ...asset.video,
          availableThumbnails: [...(asset.video.availableThumbnails ?? [])],
        },
      }
    case 'gif':
      return { kind: 'gif', gif: { ...asset.gif } }
    case 'document':
      return { kind: 'document', document: { ...asset.document } }
    case 'link': {
      const at = asset.link.availableThumbnails
      let clonedThumbnails: Image[] | string[] | null = null
      if (Array.isArray(at)) {
        if (at.length === 0) {
          clonedThumbnails = []
        } else if (isStringArray(at)) {
          clonedThumbnails = at.slice()
        } else {
          clonedThumbnails = at.map(cloneImage)
        }
      }
      return {
        kind: 'link',
        link: {
          ...asset.link,
          thumbnail: asset.link.thumbnail
            ? cloneImage(asset.link.thumbnail)
            : null,
          availableThumbnails: clonedThumbnails,
        },
      }
    }
  }
}

/**
 * Defensive parse of persisted/unknown data into a valid OmniDraft.
 * Starts from an empty draft and copies over only recognized fields
 * with correct runtime types, dropping unrecognized entries.
 */
export function hydrateOmni(data: unknown): OmniDraft {
  const result = createEmptyOmniDraft()

  if (!isObject(data)) {
    return result
  }

  const raw: Record<string, unknown> = data

  if (typeof raw['text'] === 'string') {
    result.text = raw['text']
  }

  if (Array.isArray(raw['urls'])) {
    result.urls = raw['urls'].filter((u): u is string => typeof u === 'string')
  }

  if (Array.isArray(raw['unshortenedUrls'])) {
    result.unshortenedUrls = raw['unshortenedUrls'].filter(
      (u): u is string => typeof u === 'string',
    )
  }

  if (Array.isArray(raw['assets'])) {
    result.assets = raw['assets'].reduce<OmniAsset[]>((acc, entry) => {
      const asset = hydrateAsset(entry)
      if (asset !== null) acc.push(asset)
      return acc
    }, [])
  }

  if (typeof raw['aiAssisted'] === 'boolean') {
    result.aiAssisted = raw['aiAssisted']
  }

  if (typeof raw['draftId'] === 'string') {
    result.draftId = raw['draftId']
  } else if (raw['draftId'] === null) {
    result.draftId = null
  }

  if (typeof raw['title'] === 'string') {
    result.title = raw['title']
  } else if (raw['title'] === null) {
    result.title = null
  }

  return result
}

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function isStringOrNull(v: unknown): v is string | null {
  return typeof v === 'string' || v === null
}

/**
 * True for actual numbers and non-empty strings that parse to a finite number.
 * Explicitly rejects empty/whitespace strings because Number('') === 0, which
 * would silently accept a missing dimension as zero.
 */
function isNumberLike(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v)
  if (typeof v === 'string' && v.trim() !== '')
    return Number.isFinite(Number(v))
  return false
}

function isNumberLikeOrNull(v: unknown): boolean {
  return v === null || isNumberLike(v)
}

function toNumber(v: number | string): number {
  return Number(v)
}

function toNumberOrNull(v: number | string | null): number | null {
  return v === null ? null : Number(v)
}

// --- Raw types: accepted during hydration. Numeric fields may arrive as
// strings from legacy auto-saved drafts. The normalize* functions coerce them
// to actual numbers so the resulting object matches the declared entity type.

type RawImage = {
  url: string
  width: number | string | null
  height: number | string | null
  altText?: string | null
  source?: Image['source']
  mediaType: string
  sourceLink?: string
  originalUrl?: string | null
  userTags?: Image['userTags']
}

function isRawImage(v: unknown): v is RawImage {
  return (
    isObject(v) &&
    typeof v['url'] === 'string' &&
    isNumberLikeOrNull(v['width']) &&
    isNumberLikeOrNull(v['height']) &&
    typeof v['mediaType'] === 'string'
  )
}

function normalizeImage(raw: RawImage): Image {
  return {
    url: raw.url,
    width: toNumberOrNull(raw.width),
    height: toNumberOrNull(raw.height),
    mediaType: raw.mediaType,
    ...(raw.altText !== undefined ? { altText: raw.altText } : {}),
    ...(raw.source !== undefined ? { source: raw.source } : {}),
    ...(raw.sourceLink !== undefined ? { sourceLink: raw.sourceLink } : {}),
    ...(raw.originalUrl !== undefined ? { originalUrl: raw.originalUrl } : {}),
    ...(raw.userTags !== undefined ? { userTags: raw.userTags } : {}),
  }
}

type RawVideo = {
  uploadId: string | null
  name: string
  duration: number | string
  durationMs: number | string
  size: number | string
  width: number | string
  height: number | string
  frameRate?: number
  audioCodec?: string | null
  url: string
  originalUrl: string
  thumbnail: string
  availableThumbnails: string[]
  thumbOffset?: number | string
  mediaType: string
  wasEdited?: boolean
}

function isRawVideo(v: unknown): v is RawVideo {
  return (
    isObject(v) &&
    isStringOrNull(v['uploadId']) &&
    typeof v['name'] === 'string' &&
    isNumberLike(v['duration']) &&
    isNumberLike(v['durationMs']) &&
    isNumberLike(v['size']) &&
    isNumberLike(v['width']) &&
    isNumberLike(v['height']) &&
    typeof v['url'] === 'string' &&
    typeof v['originalUrl'] === 'string' &&
    typeof v['thumbnail'] === 'string' &&
    Array.isArray(v['availableThumbnails']) &&
    typeof v['mediaType'] === 'string'
  )
}

function normalizeVideo(raw: RawVideo): Video {
  return {
    uploadId: raw.uploadId,
    name: raw.name,
    duration: toNumber(raw.duration),
    durationMs: toNumber(raw.durationMs),
    size: toNumber(raw.size),
    width: toNumber(raw.width),
    height: toNumber(raw.height),
    url: raw.url,
    originalUrl: raw.originalUrl,
    thumbnail: raw.thumbnail,
    availableThumbnails: raw.availableThumbnails,
    mediaType: raw.mediaType,
    ...(raw.frameRate !== undefined ? { frameRate: raw.frameRate } : {}),
    ...(raw.audioCodec === null || typeof raw.audioCodec === 'string'
      ? { audioCodec: raw.audioCodec }
      : {}),
    ...(raw.thumbOffset !== undefined ? { thumbOffset: raw.thumbOffset } : {}),
    ...(raw.wasEdited !== undefined ? { wasEdited: raw.wasEdited } : {}),
  }
}

type RawGif = {
  url: string
  stillGifUrl: string
  mediaType: string
  width: number | string | null
  height: number | string | null
  frameCount?: number | null
}

function isRawGif(v: unknown): v is RawGif {
  return (
    isObject(v) &&
    typeof v['url'] === 'string' &&
    typeof v['stillGifUrl'] === 'string' &&
    typeof v['mediaType'] === 'string' &&
    isNumberLikeOrNull(v['width']) &&
    isNumberLikeOrNull(v['height'])
  )
}

function normalizeGif(raw: RawGif): Gif {
  return {
    url: raw.url,
    stillGifUrl: raw.stillGifUrl,
    mediaType: raw.mediaType,
    width: toNumberOrNull(raw.width),
    height: toNumberOrNull(raw.height),
    ...(raw.frameCount !== undefined ? { frameCount: raw.frameCount } : {}),
  }
}

type RawDocument = {
  id: string
  url: string
  title: string
  mediaType: Document['mediaType']
  pageCount: number | string | null
  thumbnailUrl?: string
  fileSizeBytes?: number
}

function isRawDocument(v: unknown): v is RawDocument {
  return (
    isObject(v) &&
    typeof v['id'] === 'string' &&
    typeof v['url'] === 'string' &&
    typeof v['title'] === 'string' &&
    v['mediaType'] === 'DOCUMENT' &&
    isNumberLikeOrNull(v['pageCount'])
  )
}

function normalizeDocument(raw: RawDocument): Document {
  return {
    id: raw.id,
    url: raw.url,
    title: raw.title,
    mediaType: raw.mediaType,
    pageCount: toNumberOrNull(raw.pageCount),
    ...(raw.thumbnailUrl !== undefined
      ? { thumbnailUrl: raw.thumbnailUrl }
      : {}),
    ...(raw.fileSizeBytes !== undefined
      ? { fileSizeBytes: raw.fileSizeBytes }
      : {}),
  }
}

function isLink(v: unknown): v is Link {
  return (
    isObject(v) &&
    typeof v['url'] === 'string' &&
    typeof v['wasEdited'] === 'boolean' &&
    isStringOrNull(v['expandedUrl']) &&
    isStringOrNull(v['title']) &&
    isStringOrNull(v['description'])
  )
}

function hydrateAsset(entry: unknown): OmniAsset | null {
  if (!isObject(entry)) return null

  const kind = entry['kind']
  if (typeof kind !== 'string') return null

  switch (kind) {
    case 'image':
      return isRawImage(entry['image'])
        ? { kind: 'image', image: normalizeImage(entry['image']) }
        : null
    case 'video':
      return isRawVideo(entry['video'])
        ? { kind: 'video', video: normalizeVideo(entry['video']) }
        : null
    case 'gif':
      return isRawGif(entry['gif'])
        ? { kind: 'gif', gif: normalizeGif(entry['gif']) }
        : null
    case 'document':
      return isRawDocument(entry['document'])
        ? { kind: 'document', document: normalizeDocument(entry['document']) }
        : null
    case 'link':
      return isLink(entry['link'])
        ? { kind: 'link', link: entry['link'] }
        : null
    default:
      return null
  }
}
